import { useMemo } from 'react';
import { Calendar } from '@rocco/ui/calendar/components/calendar';
import { TimePicker } from '@rocco/ui/calendar/components/time-picker';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
} from '@rocco/components/select';
import { Clock3 } from 'lucide-react';
interface FreeDay {
    date: string;
    freeSlots: Array<{ label: string; timestamp: number }>;
}

interface BookingPhaseProps {
    timezoneName: string;
    onTimezoneChange: (timezone: string) => void;
    availableTimezones: string[];
    selectedDateTimestamp: number | null;
    onDateSelect: (timestamp: number) => void;
    freeDays: FreeDay[];
    durations: number[];
    selectedDuration: number;
    onDurationChange: (value: string) => void;
    isDurationDisabled: boolean;
    freeHours: string[];
    normalizedSelectedSlotTimestamp: number | null;
    onTimeSelect: (timestamp: number) => void;
    timePickerVariant: 'list' | 'listCompact';
    timePickerStartHour: number;
    timePickerEndHour: number;
    timePickerStepMinutes: number;
    meetingSummary: React.ReactNode;
    hasSelectedSlot: boolean;
}

/**
 * Displays the booking interface with calendar and time picker
 */
export const BookingPhase = ({
    timezoneName,
    onTimezoneChange,
    availableTimezones,
    selectedDateTimestamp,
    onDateSelect,
    freeDays,
    durations,
    selectedDuration,
    onDurationChange,
    isDurationDisabled,
    freeHours,
    normalizedSelectedSlotTimestamp,
    onTimeSelect,
    timePickerVariant,
    timePickerStartHour,
    timePickerEndHour,
    timePickerStepMinutes,
}: BookingPhaseProps) => {
    const durationOptions = useMemo(
        () =>
            Array.from(new Set(durations))
                .filter((duration): duration is number =>
                    Number.isFinite(duration),
                )
                .sort((a, b) => a - b)
                .map((duration) => ({
                    value: duration,
                    label: `${duration} minute${duration === 1 ? '' : 's'}`,
                })),
        [durations],
    );

    const disableDurationSelect =
        isDurationDisabled || durationOptions.length === 0;

    const durationSelectValue = durationOptions.some(
        (option) => option.value === selectedDuration,
    )
        ? String(selectedDuration)
        : undefined;

    const durationPlaceholder = disableDurationSelect
        ? 'No durations available'
        : 'Select duration';

    return (
        <div className="flex h-full w-full">
            <div className="relative -left-1 ml-0 w-96 pr-2">
                <Calendar
                    timeZone={timezoneName}
                    selectedTimestamp={selectedDateTimestamp ?? undefined}
                    onDateSelect={onDateSelect}
                    freeDays={freeDays}
                    naviVariant="right"
                />
            </div>
            <div className="w-103 pl-14">
                <div className="flex h-full flex-col">
                    <div className="mb-6">
                        <div className="text-4.5 font-medium tracking-wide text-gray-900">
                            Meeting duration
                        </div>
                        <div className="mt-2">
                            <Select
                                value={durationSelectValue ?? undefined}
                                onValueChange={onDurationChange}
                                disabled={disableDurationSelect}
                            >
                                <SelectTrigger
                                    className="flex h-11 w-full items-center justify-between gap-2 rounded-2 border border-transparent bg-[#f6f6f6] px-4 text-left text-base font-normal text-gray-700 disabled:cursor-not-allowed disabled:opacity-60"
                                    focusStyle="none"
                                >
                                    <Clock3
                                        className="h-4 w-4 shrink-0 text-gray-500"
                                        aria-hidden="true"
                                    />{' '}
                                    <span className="flex flex-1 justify-between text-base text-gray-500">
                                        {selectedDuration
                                            ? durationOptions.find(
                                                  (option) =>
                                                      option.value ===
                                                      selectedDuration,
                                              )?.label
                                            : (durationPlaceholder ??
                                              'No durations available')}
                                    </span>
                                </SelectTrigger>
                                {durationOptions.length ? (
                                    <SelectContent className="w-full">
                                        {durationOptions.map((option) => (
                                            <SelectItem
                                                key={option.value}
                                                value={String(option.value)}
                                            >
                                                {option.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                ) : null}
                            </Select>
                        </div>
                    </div>
                    <div className="mt-0 flex-1">
                        {typeof selectedDateTimestamp === 'number' ? (
                            <TimePicker
                                key={selectedDateTimestamp}
                                timeZone={timezoneName}
                                startTimestamp={selectedDateTimestamp}
                                onTimeSelect={onTimeSelect}
                                onTimezoneChange={onTimezoneChange}
                                availableTimezones={availableTimezones}
                                initialSelectedTime={
                                    normalizedSelectedSlotTimestamp ?? undefined
                                }
                                allowTimeZoneChange={true}
                                freeHours={freeHours}
                                startHour={timePickerStartHour}
                                endHour={timePickerEndHour}
                                stepMinutes={timePickerStepMinutes}
                                variant={timePickerVariant}
                            />
                        ) : (
                            <div className="flex h-full items-center justify-center rounded-3 border border-dashed border-gray-200 bg-gray-50 px-4 text-center text-sm text-gray-500">
                                Not available for this day.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
